import Axios from 'axios';
import { Auth } from '@aws-amplify/auth';
import {
  ACTIVE_SELLER_GROUP_HEADER,
  getValidActiveSellerGroupId,
} from '@seller-hermes/features/portal/portal-local-context';
import { hasCachedCognitoSession } from '@seller-hermes/utils/cognito-session-cache';
import { redirectAfterSessionLoss } from '@seller-hermes/utils/auth-session-expiry';
import { withTimeout } from '@seller-hermes/utils/promise-timeout';
import {
  installUiContext,
  uiContextHeaders,
  uiContextRequestDone,
} from '@seller-hermes/utils/ui-context';
import { installDevRoute } from '@seller-hermes/utils/dev-route';

const AUTH_SESSION_TIMEOUT_MS = 5000;

// Tela e ação do usuário em cada request (ver ui-context.ts): vira
// `abbiamo.ui.screen` / `abbiamo.ui.action` nos logs e spans do backend.
installUiContext();

// /portal/me is the discovery call that tells us which seller groups the
// authenticated user actually belongs to — it must never carry a
// not-yet-validated X-Active-Seller-Group value (that's the exact request
// that leaked a foreign-account id in the LOG-475 incident).
const PORTAL_ME_PATH = '/portal/me';

const api = Axios.create({
  baseURL: process.env['NX_API_URL'],
  timeout: 15000,
});

api.interceptors.request.use(async (config) => {
  const ui = uiContextHeaders(config);
  config.headers ? Object.assign(config.headers, ui) : (config.headers = ui);

  const activeSellerGroupId =
    config.url === PORTAL_ME_PATH ? null : getValidActiveSellerGroupId();

  if (activeSellerGroupId) {
    config.headers
      ? (config.headers[ACTIVE_SELLER_GROUP_HEADER] = activeSellerGroupId)
      : (config.headers = {
          [ACTIVE_SELLER_GROUP_HEADER]: activeSellerGroupId,
        });
  }

  if (!hasCachedCognitoSession()) return config;

  try {
    const auth = await withTimeout(
      Auth.currentSession(),
      AUTH_SESSION_TIMEOUT_MS,
      'Cognito session check timed out',
    );
    const token = auth.getIdToken().getJwtToken();
    if (token) {
      config.headers
        ? (config.headers['authorization'] = `Bearer ${token}`)
        : (config.headers = { authorization: `Bearer ${token}` });
    }
  } catch {
    // A previously cached session that no longer resolves means the
    // session was invalidated — most commonly, signed out from another
    // product tab on the shared Cognito cookie. Force this tab back to
    // login rather than sending the request unauthenticated.
    redirectAfterSessionLoss();
    return config;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    uiContextRequestDone(
      response?.config,
      response?.status,
      undefined,
      response?.headers?.['x-abbiamo-time'],
    );
    return response;
  },
  (error) => {
    uiContextRequestDone(
      error?.config,
      error?.response?.status,
      error,
      error?.response?.headers?.['x-abbiamo-time'],
    );
    if (error?.response?.status === 401) {
      redirectAfterSessionLoss();
    }
    return Promise.reject(error);
  },
);

// Rota de dev (`x-dev-route`), só fora de prod. Ver utils/dev-route.ts.
installDevRoute(api);

export default api;
